import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { PencilLine, Undo2, Wallet, X } from 'lucide-react';
import { formatIqd, fromMillidinars } from '@gc/shared';

import type { CustomerOrderHistoryRow } from '@gc/shared';

import { useAuth } from '@/features/auth/useAuth';
import { BalanceRefundDialog } from './BalanceRefundDialog';
import { CustomerFormDialog } from './CustomerFormDialog';
import { RefillDialog } from './RefillDialog';
import { RefundOrderDialog } from './RefundOrderDialog';
import { useCustomerProfile } from './useCustomers';

interface CustomerProfileDrawerProps {
  customerId: string;
  currencySymbol: string;
  onClose: () => void;
  onChanged: () => void;
}

type Tab = 'play' | 'orders' | 'refills' | 'points' | 'cards';

const TABS: readonly Tab[] = ['play', 'orders', 'refills', 'points', 'cards'];

/**
 * Refund Order (owner, 2026-08-12): PAID orders only — an OPEN one is simply
 * not charged at the till — and only while something is left to give back. A
 * refund row itself is never refundable.
 */
function isRefundableOrder(row: CustomerOrderHistoryRow): boolean {
  return (
    !row.isRefund &&
    row.billStatus === 'PAID' &&
    row.items.some((item) => item.quantity - item.refundedQuantity > 0)
  );
}

/**
 * The account profile — SPEC §5.5: "balance, points, play history, order
 * history, refill history", plus the card-login trail behind the §5.7 Cards
 * report.
 */
export function CustomerProfileDrawer({
  customerId,
  currencySymbol,
  onClose,
  onChanged,
}: CustomerProfileDrawerProps) {
  const { t } = useTranslation();
  const { can } = useAuth();

  const { data: profile, loading, reload } = useCustomerProfile(customerId);
  const [tab, setTab] = useState<Tab>('play');
  const [refilling, setRefilling] = useState(false);
  const [refunding, setRefunding] = useState(false);
  const [refundingOrder, setRefundingOrder] = useState<CustomerOrderHistoryRow | null>(null);
  const [editing, setEditing] = useState(false);
  /*
   * Refund Order gives money back for a sale already paid, so its route wants
   * `refunds.process` on top of `cafe.return` (2026-09-24). The button stays
   * on every refundable row — a missing action reads as a broken screen — but
   * is shut, naming both keys, for anyone the server would refuse.
   */
  const canRefundOrder = can('cafe.return') && can('refunds.process');

  const money = (value: string) => formatIqd(fromMillidinars(value), { currencySymbol });

  function handleMoneyMoved(): void {
    void reload();
    onChanged();
  }

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/50" onClick={onClose} role="presentation" />

      <aside
        role="dialog"
        aria-modal="true"
        aria-label={profile?.fullName ?? t('customers.title')}
        className="fixed inset-y-0 z-50 flex w-full max-w-2xl flex-col border-s border-subtle bg-ground shadow-2xl"
        style={{ insetInlineEnd: 0 }}
      >
        <header className="flex items-center gap-3 border-b border-subtle p-4">
          <button
            type="button"
            onClick={onClose}
            aria-label={t('common.cancel')}
            className="rounded-control p-1 text-content-secondary hover:bg-hover hover:text-content"
          >
            <X className="h-5 w-5" aria-hidden />
          </button>
          <h2 className="flex-1 truncate text-base font-semibold text-content">
            {profile?.fullName ?? '…'}
          </h2>

          {profile && can('customers.manage') ? (
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="flex items-center gap-1.5 rounded-full border border-subtle px-3 py-1.5 text-xs text-content-secondary hover:bg-hover"
            >
              <PencilLine className="h-4 w-4" aria-hidden />
              {t('common.edit')}
            </button>
          ) : null}

          {profile && can('customers.refill') ? (
            <button
              type="button"
              onClick={() => setRefilling(true)}
              className="flex items-center gap-1.5 rounded-full bg-primary px-3 py-1.5 text-xs font-medium text-primary-fg hover:bg-primary-hover"
            >
              <Wallet className="h-4 w-4" aria-hidden />
              {t('customers.refill.title')}
            </button>
          ) : null}

          {profile ? (
            <button
              type="button"
              onClick={() => setRefunding(true)}
              // Never hidden: a cashier who cannot find the action assumes the
              // software is broken. The dialog names the gate that is closed.
              className="flex items-center gap-1.5 rounded-full border border-subtle px-3 py-1.5 text-xs text-content-secondary hover:bg-hover"
            >
              <Undo2 className="h-4 w-4" aria-hidden />
              {t('customers.refund.title')}
            </button>
          ) : null}
        </header>

        {loading && !profile ? (
          <p className="p-4 text-sm text-content-muted">{t('common.loading')}</p>
        ) : !profile ? (
          <p className="p-4 text-sm text-danger">{t('customers.loadFailed')}</p>
        ) : (
          <div className="min-h-0 flex-1 overflow-auto p-4">
            <dl className="grid grid-cols-2 gap-px overflow-hidden rounded-card bg-subtle sm:grid-cols-3">
              <Stat label={t('customers.balance')} value={money(profile.balanceMd)} emphasis />
              {/* The play-cashback wallet, read out both ways: what it is
                  worth, and how long it plays for at the venue's PC rate —
                  the second is the number the counter is actually asked. */}
              <Stat
                label={t('customers.bonusTime')}
                value={profile.timeCreditDuration ?? money(profile.timeCreditMd)}
                emphasis
              />
              <Stat label={t('customers.playCredit')} value={money(profile.timeCreditMd)} />
              <Stat label={t('customers.points')} value={String(profile.points)} />
              <Stat
                label={t('customers.profile.visits')}
                value={String(profile.totals.visitCount)}
              />
              {/* Wave 3 (§5.6): the discount buckets replacing the old single
                  percent — reservations, cafe, kitchen and shisha. */}
              <Stat label={t('customers.discountTime')} value={`${profile.discountTimePercent}%`} />
              <Stat label={t('customers.discountCafe')} value={`${profile.discountCafePercent}%`} />
              <Stat
                label={t('customers.discountKitchen')}
                value={`${profile.discountKitchenPercent}%`}
              />
              <Stat
                label={t('customers.discountShisha')}
                value={`${profile.discountShishaPercent}%`}
              />
            </dl>

            <dl className="mt-3 grid grid-cols-2 gap-px overflow-hidden rounded-card bg-subtle sm:grid-cols-4">
              <Stat
                label={t('customers.profile.lifetimeSpend')}
                value={money(profile.totals.spendMd)}
              />
              <Stat
                label={t('customers.profile.lifetimeRefill')}
                value={money(profile.totals.refillMd)}
              />
              <Stat
                label={t('customers.profile.lifetimeBonus')}
                value={money(profile.totals.bonusMd)}
              />
              <Stat
                label={t('customers.profile.lifetimeRefunded')}
                value={money(profile.totals.refundedMd)}
              />
            </dl>

            {/*
              What they have open RIGHT NOW, above the history tabs.
              The tabs below are history — closed sittings and paid orders — and
              history is the wrong answer to the question staff ask when they
              click a name at the till. Hidden entirely when they owe nothing, so
              it never becomes a permanently empty panel.
            */}
            {profile.open.sessions.length + profile.open.orders.length > 0 ? (
              <section
                data-open-items
                className="mt-3 overflow-hidden rounded-card border border-ending/40 bg-ending/5"
              >
                <header className="flex items-center gap-2 border-b border-ending/30 px-3 py-2">
                  <span className="flex-1 text-sm font-semibold text-content">
                    {t('customers.profile.openNow')}
                  </span>
                  <span className="tabular text-sm font-semibold text-ending">
                    {money(profile.open.totalMd)}
                  </span>
                </header>
                <ul className="divide-y divide-subtle/60">
                  {profile.open.sessions.map((session) => (
                    <li
                      key={session.sessionId}
                      className="flex items-center gap-2 px-3 py-1.5 text-sm"
                    >
                      <span className="flex-1 truncate text-content">{session.deviceName}</span>
                      <span className="tabular text-xs text-content-muted">{session.duration}</span>
                      <span
                        className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${
                          session.status === 'ACTIVE'
                            ? 'bg-available/15 text-available'
                            : session.status === 'PAUSED'
                              ? 'bg-ending/15 text-ending'
                              : 'bg-hover text-content-muted'
                        }`}
                      >
                        {t(`customers.profile.openStatus.${session.status.toLowerCase()}`, {
                          defaultValue: session.status,
                        })}
                      </span>
                      <span className="tabular w-20 text-end text-content">
                        {money(session.totalMd)}
                      </span>
                    </li>
                  ))}
                  {profile.open.orders.map((order) => (
                    <li key={order.billId} className="flex items-center gap-2 px-3 py-1.5 text-sm">
                      <span className="flex-1 truncate text-content">
                        {order.itemSummary || order.invoiceNumber}
                      </span>
                      <span className="tabular w-20 text-end text-content">
                        {money(order.totalMd)}
                      </span>
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}

            <div className="mt-4 flex flex-wrap gap-1 border-b border-subtle">
              {TABS.map((entry) => (
                <button
                  key={entry}
                  type="button"
                  onClick={() => setTab(entry)}
                  aria-pressed={tab === entry}
                  className={[
                    'rounded-t-control px-3 py-2 text-sm transition-colors',
                    tab === entry
                      ? 'border-b-2 border-primary font-medium text-content'
                      : 'text-content-secondary hover:bg-hover',
                  ].join(' ')}
                >
                  {t(
                    `customers.profile.${entry === 'points' ? 'pointsHistory' : entry === 'cards' ? 'cardLogins' : entry}`,
                  )}
                </button>
              ))}
            </div>

            <div className="mt-3">
              {tab === 'play' ? (
                <HistoryTable
                  head={[
                    t('bills.columns.invoiceNumber'),
                    t('customers.profile.device'),
                    t('common.from'),
                    t('customers.profile.duration'),
                    t('common.total'),
                  ]}
                  rows={profile.play.map((row) => [
                    row.invoiceNumber ?? '—',
                    row.deviceName,
                    new Date(row.startedAt).toLocaleString(),
                    row.duration,
                    money(row.totalMd),
                  ])}
                />
              ) : tab === 'orders' ? (
                <>
                  {/* The refusal, printed once above the table. The shut
                      Refund buttons carry it as a title too, but a title needs
                      a hover and a phone has none — a greyed Refund with no
                      reason is the broken-looking screen the button is kept
                      on the row to avoid. Only when a row could be refunded. */}
                  {!canRefundOrder && profile.orders.some(isRefundableOrder) ? (
                    <p data-order-refund-refusal className="mb-2 text-xs text-content-muted">
                      {t('customers.orderRefund.noPermission')}
                    </p>
                  ) : null}
                  <HistoryTable
                    head={[
                      t('bills.columns.invoiceNumber'),
                      t('common.from'),
                      t('customers.profile.items'),
                      t('common.total'),
                      '',
                    ]}
                    rows={profile.orders.map((row) => [
                      row.invoiceNumber ?? '—',
                      new Date(row.placedAt).toLocaleString(),
                      row.itemSummary || '—',
                      money(row.totalMd),
                      /* Refund Order on the rows `isRefundableOrder` allows;
                         a refund row itself gets a tag instead of a button. */
                      row.isRefund ? (
                        <span className="rounded-full bg-overlay px-2 py-0.5 text-[11px] text-content-muted">
                          {t('customers.orderRefund.tag')}
                        </span>
                      ) : isRefundableOrder(row) ? (
                        <button
                          type="button"
                          data-refund-order={row.orderId}
                          disabled={!canRefundOrder}
                          title={
                            canRefundOrder ? undefined : t('customers.orderRefund.noPermission')
                          }
                          onClick={() => setRefundingOrder(row)}
                          className="rounded-full border border-subtle px-2.5 py-1 text-xs text-content-secondary hover:bg-hover disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent"
                        >
                          {t('customers.orderRefund.title')}
                        </button>
                      ) : (
                        ''
                      ),
                    ])}
                  />
                </>
              ) : tab === 'refills' ? (
                <HistoryTable
                  head={[
                    t('bills.columns.invoiceNumber'),
                    t('common.from'),
                    t('customers.profile.deposit'),
                    t('customers.profile.bonus'),
                    t('customers.profile.credited'),
                    t('customers.profile.balanceAfter'),
                    t('customers.profile.by'),
                  ]}
                  rows={profile.refills.map((row) => [
                    row.invoiceNumber ?? '—',
                    new Date(row.createdAt).toLocaleString(),
                    // A payout is shown as a negative deposit so the column
                    // reads as a running movement rather than two directions
                    // wearing the same sign.
                    row.isRefund
                      ? `− ${money(row.amountMd)}`
                      : row.method === 'ELECTRONIC'
                        ? // "20,000 · FIB": deposited through a provider, not in notes.
                          `${money(row.amountMd)} · ${row.electronicProviderLabel ?? t('payments.electronic')}`
                        : money(row.amountMd),
                    money(row.bonusMd),
                    row.isRefund ? '—' : money(row.totalCreditedMd),
                    money(row.balanceAfterMd),
                    row.employeeName ?? '—',
                  ])}
                />
              ) : tab === 'points' ? (
                <HistoryTable
                  head={[
                    t('common.from'),
                    t('customers.points'),
                    t('customers.profile.balanceAfter'),
                    t('customers.pointsAdjust.reason'),
                  ]}
                  rows={profile.pointsHistory.map((row) => [
                    new Date(row.createdAt).toLocaleString(),
                    row.points > 0 ? `+${row.points}` : String(row.points),
                    String(row.balanceAfter),
                    row.reason ?? '—',
                  ])}
                />
              ) : (
                <HistoryTable
                  head={[
                    t('customers.card.when'),
                    t('customers.card.cardId'),
                    t('customers.card.device'),
                    t('customers.card.result'),
                  ]}
                  rows={profile.cardLogins.map((row) => [
                    new Date(row.createdAt).toLocaleString(),
                    row.cardId,
                    row.deviceName ?? '—',
                    row.succeeded
                      ? t('customers.profile.matched')
                      : t('customers.profile.unmatched'),
                  ])}
                />
              )}
            </div>

            {profile.note ? (
              <p className="mt-4 rounded-card bg-raised p-3 text-sm text-content-secondary">
                {profile.note}
              </p>
            ) : null}

            <p className="mt-3 text-xs text-content-muted">
              {t('customers.registered')}: {new Date(profile.registeredAt).toLocaleDateString()}
              {profile.lastLoginAt
                ? ` · ${t('customers.lastLogin')}: ${new Date(profile.lastLoginAt).toLocaleString()}`
                : ''}
              {profile.cardId ? ` · ${t('customers.cardId')}: ${profile.cardId}` : ''}
            </p>
          </div>
        )}
      </aside>

      {profile && refilling ? (
        <RefillDialog
          customer={profile}
          currencySymbol={currencySymbol}
          onClose={() => setRefilling(false)}
          onDone={handleMoneyMoved}
        />
      ) : null}

      {profile && refunding ? (
        <BalanceRefundDialog
          customer={profile}
          currencySymbol={currencySymbol}
          onClose={() => setRefunding(false)}
          onDone={handleMoneyMoved}
        />
      ) : null}

      {profile && refundingOrder ? (
        <RefundOrderDialog
          customerId={customerId}
          order={refundingOrder}
          currencySymbol={currencySymbol}
          onClose={() => setRefundingOrder(null)}
          onDone={handleMoneyMoved}
        />
      ) : null}

      {profile && editing ? (
        <CustomerFormDialog
          customer={profile}
          onClose={() => setEditing(false)}
          onSaved={handleMoneyMoved}
        />
      ) : null}
    </>
  );
}

function Stat({ label, value, emphasis }: { label: string; value: string; emphasis?: boolean }) {
  return (
    <div className="bg-raised px-3 py-2">
      <dt className="text-xs text-content-secondary">{label}</dt>
      <dd
        className={`tabular ${emphasis ? 'text-lg font-semibold text-content' : 'text-sm text-content-secondary'}`}
      >
        {value}
      </dd>
    </div>
  );
}

function HistoryTable({ head, rows }: { head: string[]; rows: ReactNode[][] }) {
  const { t } = useTranslation();

  if (rows.length === 0) {
    return <p className="text-sm text-content-muted">{t('customers.profile.empty')}</p>;
  }

  return (
    <div className="overflow-x-auto rounded-card border border-subtle">
      <table className="w-full text-sm">
        <thead className="bg-overlay text-[10px] uppercase tracking-[0.14em] text-content-muted">
          <tr>
            {head.map((cell) => (
              <th key={cell} className="px-3 py-2 text-start font-medium">
                {cell}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={index} className="border-t border-subtle">
              {row.map((cell, cellIndex) => (
                <td key={cellIndex} className="tabular px-3 py-2 text-content-secondary">
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
